import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  IntlProvider as _IntlProvider,
  MessageFormatElement,
} from "react-intl";
import { LinearProgress } from "@mui/material";
import { LANGUAGE, LOCALE } from "@myplant-io/user-management";
import { useAuth } from "@myplant-io/hooks";
import {
  API_BASE_URL,
  SUPPORTED_LANGUAGES,
  SUPPORTED_LOCALES,
} from "../constants";

async function loadMessages(
  language: LANGUAGE
): Promise<Record<string, MessageFormatElement[]>> {
  switch (language) {
    default:
      return (await import("../translations/locales/compiled/en.json")).default;
  }
}

export type IntlState = {
  locale: LOCALE;
  language: LANGUAGE;
  timezone: string;
};

const DEFAULT_STATE: IntlState = {
  locale: LOCALE.enUS,
  language: LANGUAGE.en,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

export const IntlContext = createContext<IntlState>(DEFAULT_STATE);

export function useIntlState() {
  return useContext(IntlContext);
}

export function IntlProvider({ children }: PropsWithChildren<unknown>) {
  const { data } = useAuth(API_BASE_URL);
  const profile = data?.user.profile;

  const locale = (SUPPORTED_LOCALES as string[]).includes(
    profile?.preferredLocale ?? ""
  )
    ? (profile!.preferredLocale as LOCALE)
    : LOCALE.enUS;

  const preferredLanguage = profile?.preferredLanguage ?? "";
  const language = (SUPPORTED_LANGUAGES as string[]).includes(preferredLanguage)
    ? (preferredLanguage as LANGUAGE)
    : LANGUAGE.en;

  const timezone =
    profile?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [messages, setMessages] = useState<
    Record<string, MessageFormatElement[]> | undefined
  >(undefined);
  const [loadedLanguage, setLoadedLanguage] = useState<LANGUAGE | undefined>(
    undefined
  );

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        const loaded = await loadMessages(language);
        if (!isMounted) return;
        setMessages(loaded);
        setLoadedLanguage(language);
      } catch (error) {
        console.error("Failed to load messages for language", language, error);
        if (!isMounted) return;
        // Fall back to English if the requested language bundle fails to load.
        try {
          const fallback = await loadMessages(LANGUAGE.en);
          if (!isMounted) return;
          setMessages(fallback);
          setLoadedLanguage(LANGUAGE.en);
        } catch (fallbackError) {
          console.error("Failed to load fallback messages", fallbackError);
          if (!isMounted) return;
          setMessages({});
          setLoadedLanguage(LANGUAGE.en);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [language]);

  return (
    <IntlContext.Provider value={{ locale, language, timezone }}>
      {messages ? (
        <_IntlProvider
          locale={loadedLanguage === language ? locale : LOCALE.enUS}
          messages={messages}
          defaultLocale={LOCALE.enUS}
          timeZone={timezone}
        >
          {loadedLanguage === language ? children : <LinearProgress />}
        </_IntlProvider>
      ) : (
        <LinearProgress />
      )}
    </IntlContext.Provider>
  );
}
